import React, {useMemo, useState} from 'react';
import CIcon from "@coreui/icons-react";
import {EtapaProcesso, Evento, Processo, SITUACAO_ETAPA, SituacaoEvento, dataBr, dataCurta, quandoEvento, resolvido, textoDesvio} from "./modelo";
import {COR_EVENTO} from "./calendario";

interface Props {
    processo: Processo,
    podeReprogramar: (etapa: EtapaProcesso) => boolean,
    onReprogramar: (etapa: EtapaProcesso) => void,
    onAbrirItem: (itemId: number) => void,
    onAbrirEvento: (evento: Evento) => void,
}

const DIA = 86400000;
const dia = (data: string) => Math.round(new Date(`${data.slice(0, 10)}T00:00:00Z`).getTime() / DIA);
const hojeIso = () => {
    const d = new Date();
    const dois = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`;
};
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/**
 * Cronograma do processo (#480058): por etapa, a meta original (baseline), a
 * previsao atual (com o % feito) e o realizado, numa escala de dias corridos.
 * Abrir a etapa mostra o prazo de cada item. Os eventos (nao cancelados)
 * aparecem como marcos na linha da etapa e na do item (#484909).
 */
const CronogramaAba = ({processo, podeReprogramar, onReprogramar, onAbrirItem, onAbrirEvento}: Props) => {
    const [abertas, setAbertas] = useState<Set<number>>(new Set());
    const hoje = hojeIso();

    const escala = useMemo(() => {
        const datas: string[] = [processo.data_inicio, hoje];
        processo.etapas.forEach(e => {
            datas.push(e.previsao_inicio_original, e.previsao_fim_original, e.previsao_inicio, e.previsao_fim);
            if (e.data_inicio) datas.push(e.data_inicio);
            if (e.data_fim) datas.push(e.data_fim);
            e.itens.forEach(i => datas.push(i.prazo, i.prazo_original));
        });
        (processo.eventos || []).forEach(evento => evento.situacao !== SituacaoEvento.CANCELADO && datas.push(evento.inicio.slice(0, 10)));
        const dias = datas.map(dia);
        const de = Math.min(...dias) - 2;
        const ate = Math.max(...dias) + 3;
        const meses: Array<{rotulo: string, pos: number}> = [];
        const cursor = new Date(de * DIA);
        cursor.setUTCDate(1);
        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
        while (cursor.getTime() / DIA <= ate) {
            meses.push({rotulo: `${MESES[cursor.getUTCMonth()]}/${String(cursor.getUTCFullYear()).slice(2)}`, pos: (cursor.getTime() / DIA - de) * 100 / (ate - de)});
            cursor.setUTCMonth(cursor.getUTCMonth() + 1);
        }
        return {de, total: ate - de, meses};
    }, [processo, hoje]);

    const pos = (data: string) => (dia(data) - escala.de) * 100 / escala.total;
    const faixa = (inicio: string, fim: string) => ({left: `${pos(inicio)}%`, width: `${Math.max((dia(fim) - dia(inicio) + 1) * 100 / escala.total, 0.6)}%`});

    const alterna = (id: number) => {
        const novas = new Set(abertas);
        novas.has(id) ? novas.delete(id) : novas.add(id);
        setAbertas(novas);
    };

    const linhaHoje = <span className="pr-gantt-hoje" style={{left: `${pos(hoje)}%`}} title={`Hoje, ${dataBr(hoje)}`}/>;

    const ativos = (processo.eventos || []).filter(evento => evento.situacao !== SituacaoEvento.CANCELADO);
    const marcos = (lista: Evento[]) => lista.map(evento => (
        <button type="button" key={evento.id} className={`pr-gantt-evento sit-evento-${evento.situacao}`}
                style={{left: `${pos(evento.inicio.slice(0, 10))}%`, background: COR_EVENTO[evento.tipo]}}
                title={`${evento.tipo_nome}: ${evento.titulo} · ${quandoEvento(evento.inicio)} · ${evento.situacao_nome}`}
                aria-label={`${evento.tipo_nome} ${evento.titulo}`}
                onClick={() => onAbrirEvento(evento)}/>
    ));

    return (
        <div className="pr-gantt">
            <div className="pr-gantt-legenda">
                <span><i className="base"/> Meta original</span>
                <span><i className="atual"/> Previsão atual (preenchido = % feito)</span>
                <span><i className="real"/> Realizado</span>
                <span><i className="evento"/> Evento da agenda</span>
                <span><i className="hoje"/> Hoje</span>
                <span className="pr-gantt-total">
                    Término {dataBr(processo.previsao_fim)} · meta {dataBr(processo.previsao_fim_original)}
                    <em className={processo.desvio_dias_uteis > 0 ? 'ruim' : ''}> ({textoDesvio(processo.desvio_dias_uteis)})</em>
                </span>
            </div>

            <div className="pr-gantt-grade">
                <div className="pr-gantt-linha cabecalho">
                    <div className="pr-gantt-rotulo">Etapa</div>
                    <div className="pr-gantt-trilho">
                        {escala.meses.map(m => <span key={m.rotulo} className="pr-gantt-mes" style={{left: `${m.pos}%`}}>{m.rotulo}</span>)}
                    </div>
                </div>

                {processo.etapas.map(etapa => {
                    const situacao = SITUACAO_ETAPA[etapa.situacao] || SITUACAO_ETAPA.nao_iniciada;
                    const aberta = abertas.has(etapa.id);
                    const fimReal = etapa.data_fim || (etapa.data_inicio ? hoje : null);
                    return (
                        <React.Fragment key={etapa.id}>
                            <div className={`pr-gantt-linha ${situacao.classe}`}>
                                <div className="pr-gantt-rotulo">
                                    <button type="button" className="pr-gantt-abrir" onClick={() => alterna(etapa.id)} title={aberta ? 'Esconder itens' : 'Ver prazos dos itens'}>
                                        <CIcon name={aberta ? 'cil-chevron-bottom' : 'cil-chevron-right'}/>
                                    </button>
                                    <span className="pr-gantt-nome">
                                        <strong>{etapa.ordem}. {etapa.nome}</strong>
                                        <span>
                                            {Math.round(etapa.percentual)}% · {dataCurta(etapa.previsao_inicio)}–{dataCurta(etapa.previsao_fim)}
                                            {etapa.desvio_dias_uteis !== 0 && <em className={etapa.desvio_dias_uteis > 0 ? 'ruim' : 'bom'}> {textoDesvio(etapa.desvio_dias_uteis)}</em>}
                                        </span>
                                    </span>
                                    {podeReprogramar(etapa) && !etapa.data_fim && (
                                        <button type="button" className="pr-gantt-acao" onClick={() => onReprogramar(etapa)}>Reprogramar</button>
                                    )}
                                </div>
                                <div className="pr-gantt-trilho">
                                    {escala.meses.map(m => <span key={m.rotulo} className="pr-gantt-divisa" style={{left: `${m.pos}%`}}/>)}
                                    <span className="pr-gantt-barra base" style={faixa(etapa.previsao_inicio_original, etapa.previsao_fim_original)}
                                          title={`Meta original: ${dataBr(etapa.previsao_inicio_original)} → ${dataBr(etapa.previsao_fim_original)}`}/>
                                    <span className="pr-gantt-barra atual" style={faixa(etapa.previsao_inicio, etapa.previsao_fim)}
                                          title={`Previsão atual: ${dataBr(etapa.previsao_inicio)} → ${dataBr(etapa.previsao_fim)} · ${Math.round(etapa.percentual)}%`}>
                                        <span className="feito" style={{width: `${etapa.percentual}%`}}/>
                                    </span>
                                    {etapa.data_inicio && fimReal && (
                                        <span className="pr-gantt-barra real" style={faixa(etapa.data_inicio, fimReal)}
                                              title={`Realizado: ${dataBr(etapa.data_inicio)} → ${etapa.data_fim ? dataBr(etapa.data_fim) : 'em andamento'}`}/>
                                    )}
                                    {marcos(ativos.filter(evento => evento.etapa_id === etapa.id))}
                                    {linhaHoje}
                                </div>
                            </div>
                            {aberta && etapa.itens.map(item => {
                                const feito = resolvido(item);
                                const mudou = item.prazo !== item.prazo_original;
                                return (
                                    <div key={item.id} className="pr-gantt-linha item">
                                        <div className="pr-gantt-rotulo">
                                            <button type="button" className="pr-gantt-item" onClick={() => onAbrirItem(item.id)} title="Abrir o item">
                                                {item.titulo}
                                            </button>
                                        </div>
                                        <div className="pr-gantt-trilho">
                                            {mudou && <span className="pr-gantt-ponto original" style={{left: `${pos(item.prazo_original)}%`}} title={`Meta original ${dataBr(item.prazo_original)}`}/>}
                                            <span className={`pr-gantt-ponto ${feito ? 'feito' : item.atrasado ? 'atrasado' : ''}`} style={{left: `${pos(item.prazo)}%`}}
                                                  title={`Prazo ${dataBr(item.prazo)}${feito ? ' · resolvido' : item.atrasado ? ' · atrasado' : ''}`}/>
                                            {marcos(ativos.filter(evento => evento.item_id === item.id))}
                                            {linhaHoje}
                                        </div>
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default CronogramaAba;
